import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from './queries'
import type { components } from './schema'

// Carriers we never quote, book, or record shipments with (USA-1159).
export type BlockedCarrier = components['schemas']['BlockedCarrierRead']
export type BlockedCarrierWrite = components['schemas']['BlockedCarrierWrite']

export function useBlockedCarriers() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.blockedCarriers.all,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/blocked-carriers')
      if (error) throw error
      return data
    },
  })
}

export function useSaveBlockedCarrier() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ uuid, body }: { uuid?: string; body: BlockedCarrierWrite }) => {
      const client = await getClient()
      const { data, error } = uuid
        ? await client.PUT('/dashboard/blocked-carriers/{uuid}', {
            params: { path: { uuid } },
            body,
          })
        : await client.POST('/dashboard/blocked-carriers', { body })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.blockedCarriers.all,
      })
    },
  })
}

export function useRemoveBlockedCarrier() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = queryKeys.blockedCarriers.all
  return useMutation({
    mutationFn: async (uuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/blocked-carriers/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
    },
    onMutate: async (uuid) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<BlockedCarrier[]>(key)
      queryClient.setQueryData<BlockedCarrier[]>(key, (old) =>
        old?.filter((carrier) => carrier.uuid !== uuid),
      )
      return { previous }
    },
    onError: (_error, _uuid, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
